import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useRouterState, useSearch } from '@tanstack/react-router'
import { MessageCircleIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import { Button, Card, CardContent, CardHeader, DialogPrompt, Skeleton } from '@campai/lib-web/components/new/ui'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { Page } from '@/components/Page'
import {
    canCreateCommunityContent,
    canDeleteCommunityContent,
    canModerateCommunityContent,
    isOwnCommunityContent,
} from '@/product/community/access'
import { type AttachmentInput } from '@/product/community/shared/Attachments'
import { InlineContentComposer } from '@/product/community/shared/InlineContentComposer'
import { TopicDialog } from '@/product/community/shared/TopicDialog'

import { Post } from './Post'
import { Header } from './Header'
import { MoveDialog } from './MoveDialog'

type TopicData = RouterOutput['product']['community']['topics']['getTopic']
type PostData = RouterOutput['product']['community']['topics']['posts']['listPosts']['posts'][number]

const POST_PAGE_SIZE = 30
const targetPostHashPattern = /^#?post-([a-f\d]{24})$/i

const getTargetPostId = (hash: string) => {
    const match = targetPostHashPattern.exec(hash)
    return match?.[1] ?? null
}

export const Topic = observer(() => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { categoryId, topicId } = useParams({ from: '/product/community/categories/$categoryId/topics/$topicId' })
    const listSearch = useSearch({ from: '/product/community/categories/$categoryId/topics/$topicId' })
    const hash = useRouterState({ select: (state) => state.location.hash })
    const [topic, setTopic] = useState<TopicData | null>(null)
    const [posts, setPosts] = useState<PostData[] | null>(null)
    const [postCount, setPostCount] = useState<number | null>(null)
    const [topicDialogOpen, setTopicDialogOpen] = useState(false)
    const [moveDialogOpen, setMoveDialogOpen] = useState(false)
    const [editingPost, setEditingPost] = useState<PostData | null>(null)
    const [topicDeleteOpen, setTopicDeleteOpen] = useState(false)
    const [postToDelete, setPostToDelete] = useState<PostData | null>(null)
    const [reloadVersion, setReloadVersion] = useState(0)
    const [isFollowUpdating, setIsFollowUpdating] = useState(false)
    const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false)
    const requestVersionRef = useRef(0)
    const markedTopicReadKeyRef = useRef('')
    const scrolledTargetPostRef = useRef<string | null>(null)
    const targetPostId = getTargetPostId(hash)
    const topicLocked = !!topic?.lockedAt
    const topicPinned = !!topic?.pinnedAt
    const canReply = canCreateCommunityContent() && !topicLocked
    const canModerate = canModerateCommunityContent()
    const canDelete = canDeleteCommunityContent()
    const canEditTopic = !!topic && !topicLocked && (canModerate || isOwnCommunityContent(topic.createdBy))
    const canDeleteTopic = !!topic && !topicLocked && (canDelete || isOwnCommunityContent(topic.createdBy))
    const hasMorePosts = !!posts && postCount !== null && posts.length < postCount

    useEffect(() => {
        const abortController = new AbortController()
        const requestVersion = requestVersionRef.current + 1
        requestVersionRef.current = requestVersion

        setTopic(null)
        setPosts(null)
        setPostCount(null)

        void Promise.all([
            client.product.community.topics.getTopic.query({ categoryId, topicId }, { signal: abortController.signal }),
            client.product.community.topics.posts.listPosts.query(
                {
                    categoryId,
                    topicId,
                    limit: POST_PAGE_SIZE,
                    offset: 0,
                    returnCount: true,
                    sort: { createdAt: 'desc' },
                },
                { signal: abortController.signal }
            ),
        ])
            .then(([topicResult, postsResult]) => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setTopic(topicResult)
                setPosts([...postsResult.posts].reverse())
                setPostCount(postsResult.count ?? postsResult.posts.length)
            })
            .catch(() => {
                if (!abortController.signal.aborted && requestVersionRef.current === requestVersion) {
                    setPosts([])
                    setPostCount(0)
                }
            })

        return () => abortController.abort()
    }, [reloadVersion, categoryId, topicId])

    useEffect(() => {
        if (!topic || !posts) {
            return
        }

        const markReadKey = `${topic._id.toString()}:${topic.latestActivityAt.getTime().toString()}`

        if (markedTopicReadKeyRef.current === markReadKey) {
            return
        }

        markedTopicReadKeyRef.current = markReadKey

        void client.product.community.topics.markTopicRead.mutate({ categoryId, topicId }).catch(() => {
            if (markedTopicReadKeyRef.current === markReadKey) {
                markedTopicReadKeyRef.current = ''
            }
        })
    }, [categoryId, posts, topic, topicId])

    const reload = () => setReloadVersion((currentVersion) => currentVersion + 1)

    const updateTopic = async (input: { title: string; message: string; attachments: AttachmentInput }) => {
        await client.product.community.topics.updateTopic.mutate({
            categoryId,
            topicId,
            ...input,
        })
        reload()
    }

    const deleteTopic = async () => {
        await client.product.community.topics.deleteTopic.mutate({
            categoryId,
            topicId,
        })
        await navigate({
            to: '/product/community/categories/$categoryId',
            params: { categoryId },
            search: listSearch,
        })
    }

    const setTopicPinned = async (pinned: boolean) => {
        await client.product.community.topics.setTopicPinned.mutate({
            categoryId,
            topicId,
            pinned,
        })
        reload()
    }

    const setTopicLocked = async (locked: boolean) => {
        await client.product.community.topics.setTopicLocked.mutate({
            categoryId,
            topicId,
            locked,
        })
        reload()
    }

    const moveTopic = async (targetCategoryId: string) => {
        await client.product.community.topics.moveTopic.mutate({
            categoryId,
            topicId,
            targetCategoryId,
        })
        await navigate({
            to: '/product/community/categories/$categoryId/topics/$topicId',
            params: { categoryId: targetCategoryId, topicId },
        })
    }

    const setTopicFollowed = async (followed: boolean) => {
        if (!topic || isFollowUpdating) {
            return
        }

        setIsFollowUpdating(true)

        try {
            if (followed) {
                await client.product.community.topics.followTopic.mutate({
                    categoryId,
                    topicId,
                })
            } else {
                await client.product.community.topics.unfollowTopic.mutate({
                    categoryId,
                    topicId,
                })
            }

            setTopic({ ...topic, followedByCurrentUser: followed })
        } finally {
            setIsFollowUpdating(false)
        }
    }

    const createPost = async (input: { message: string; attachments: AttachmentInput }) => {
        const createdPost = await client.product.community.topics.posts.createPost.mutate({
            categoryId,
            topicId,
            ...input,
        })

        setPosts((currentPosts) => [...(currentPosts || []), createdPost])
        setPostCount((currentCount) => (currentCount === null ? currentCount : currentCount + 1))
        setTopic((currentTopic) =>
            currentTopic
                ? {
                      ...currentTopic,
                      postsCount: currentTopic.postsCount + 1,
                      latestActivityAt: createdPost.createdAt || currentTopic.latestActivityAt,
                      followedByCurrentUser: true,
                  }
                : currentTopic
        )
        setEditingPost(null)
    }

    const updatePost = async (post: PostData, input: { message: string; attachments: AttachmentInput }) => {
        await client.product.community.topics.posts.updatePost.mutate({
            categoryId,
            topicId,
            postId: post._id.toString(),
            ...input,
        })

        setEditingPost(null)
        reload()
    }

    const deletePost = async () => {
        if (!postToDelete) {
            return
        }

        await client.product.community.topics.posts.deletePost.mutate({
            categoryId,
            topicId,
            postId: postToDelete._id.toString(),
        })
        setPostToDelete(null)
        reload()
    }

    const loadMorePosts = useCallback(async () => {
        if (!posts || isLoadingMorePosts) {
            return
        }

        setIsLoadingMorePosts(true)

        try {
            const result = await client.product.community.topics.posts.listPosts.query({
                categoryId,
                topicId,
                limit: POST_PAGE_SIZE,
                offset: posts.length,
                returnCount: true,
                sort: { createdAt: 'desc' },
            })

            setPosts([...result.posts].reverse().concat(posts))
            setPostCount(result.count ?? posts.length + result.posts.length)
        } finally {
            setIsLoadingMorePosts(false)
        }
    }, [categoryId, isLoadingMorePosts, posts, topicId])

    useEffect(() => {
        if (!targetPostId) {
            scrolledTargetPostRef.current = null
            return
        }

        if (!posts) {
            return
        }

        const targetPostLoaded = posts.some((post) => post._id.toString() === targetPostId)

        if (!targetPostLoaded) {
            scrolledTargetPostRef.current = null

            if (hasMorePosts && !isLoadingMorePosts) {
                void loadMorePosts()
            }

            return
        }

        if (scrolledTargetPostRef.current === targetPostId) {
            return
        }

        scrolledTargetPostRef.current = targetPostId
        window.requestAnimationFrame(() => {
            document.getElementById(`post-${targetPostId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        })
    }, [targetPostId, posts, hasMorePosts, isLoadingMorePosts, loadMorePosts])

    return (
        <Page
            title={topic?.title || t('product.community.topic.loading')}
            parentLink={{
                to: '/product/community/categories/$categoryId',
                params: { categoryId },
                search: listSearch,
            }}
            className="flex min-h-full flex-col space-y-6 pb-8"
        >
            <Header
                topic={topic}
                topicLocked={topicLocked}
                topicPinned={topicPinned}
                isFollowUpdating={isFollowUpdating}
                setTopicFollowed={setTopicFollowed}
                canModerate={canModerate}
                setTopicPinned={setTopicPinned}
                setTopicLocked={setTopicLocked}
                setMoveDialogOpen={setMoveDialogOpen}
                canEditTopic={canEditTopic}
                setTopicDialogOpen={setTopicDialogOpen}
                canDeleteTopic={canDeleteTopic}
                setTopicDeleteOpen={setTopicDeleteOpen}
            />
            <div className="mx-auto flex w-full max-w-4xl flex-wrap items-center justify-between gap-3">
                <h2 className="text-xl font-semibold tracking-tight">{t('product.community.topic.replies')}</h2>
            </div>
            <div className="mx-auto w-full max-w-4xl space-y-3">
                {!posts &&
                    Array.from({ length: 2 }).map((_, index) => (
                        <Card key={`community-post-skeleton-${index.toString()}`} className="gap-0 p-0">
                            <CardHeader className="p-5">
                                <Skeleton className="h-4 w-1/3" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                            </CardHeader>
                        </Card>
                    ))}
                {posts?.length === 0 && (
                    <Card>
                        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                            <MessageCircleIcon className="text-muted-foreground size-8" />
                            <div className="font-medium">{t('product.community.topic.empty.title')}</div>
                            <div className="text-muted-foreground max-w-md text-sm">
                                {t('product.community.topic.empty.description')}
                            </div>
                        </CardContent>
                    </Card>
                )}
                {posts?.map((post) => (
                    <Post
                        key={post._id.toString()}
                        post={post}
                        topicLocked={topicLocked}
                        canModerate={canModerate}
                        canDelete={canDelete}
                        editingPost={editingPost}
                        topic={topic}
                        targetPostId={targetPostId}
                        updatePost={updatePost}
                        setEditingPost={setEditingPost}
                        setPostToDelete={setPostToDelete}
                    />
                ))}
                {hasMorePosts && (
                    <div className="flex justify-center pt-1">
                        <Button variant="outline" loading={isLoadingMorePosts} onClick={() => void loadMorePosts()}>
                            {t('actions.loadMore')}
                        </Button>
                    </div>
                )}
                {canReply && posts && (
                    <InlineContentComposer
                        key={`create-post-${topicId}`}
                        messageLabel={t('product.community.topic.postDialog.message')}
                        submitLabel={t('product.community.topic.postDialog.create')}
                        onSubmit={createPost}
                    />
                )}
            </div>
            <TopicDialog
                open={topicDialogOpen}
                topic={topic}
                onOpenChange={setTopicDialogOpen}
                onSubmit={updateTopic}
            />
            <MoveDialog
                open={moveDialogOpen}
                currentCategoryId={categoryId}
                onOpenChange={setMoveDialogOpen}
                onSubmit={moveTopic}
            />
            <DialogPrompt
                open={topicDeleteOpen}
                onOpenChange={setTopicDeleteOpen}
                title={t('product.community.topic.deleteDialog.title')}
                message={t('product.community.topic.deleteDialog.message', { title: topic?.title || '' })}
                confirmLabel={t('product.community.topic.deleteDialog.confirm')}
                cancelLabel={t('common.cancel')}
                confirmVariant="destructive"
                onConfirm={deleteTopic}
            />
            <DialogPrompt
                open={!!postToDelete}
                onOpenChange={(open) => {
                    if (!open) {
                        setPostToDelete(null)
                    }
                }}
                title={t('product.community.topic.post.deleteDialog.title')}
                message={t('product.community.topic.post.deleteDialog.message')}
                confirmLabel={t('product.community.topic.post.deleteDialog.confirm')}
                cancelLabel={t('common.cancel')}
                confirmVariant="destructive"
                onConfirm={deletePost}
            />
        </Page>
    )
})
